// The application form: six sections in the questionnaire's one-screen-at-a-time
// style. Drafts autosave on every section change (POST without submit); Submit runs
// the client-side mirror of the server's required-field gate first.
import { useMemo, useRef, useState } from 'react';
import { PrimaryButton, SelectRow, card } from '../components/ui';
import { ProviderShell, ScreenHeader } from './ProviderShell';
import { Field, TextInput, TextArea, ChipMultiSelect, TagInput, CheckRow } from './fields';
import {
  SECTIONS,
  SPECIALTIES,
  SESSION_DURATIONS,
  missingRequirements,
  toPayload,
  type MissingField,
} from '../lib/providerApply';
import {
  saveApplication,
  uploadPhoto,
  PHOTO_CONTENT_TYPES,
  PHOTO_MAX_BYTES,
  type ProviderApplication,
  type ProviderApplicationFields,
  type ExpertiseArea,
} from '../lib/providerApi';
import { ApiError, RateLimited } from '../lib/api';
import { US_STATE_NAMES } from '../lib/zipState';
import { INSURANCE_CARRIERS } from '../lib/insurance';
import { track } from '../lib/analytics';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function ApplicationForm({
  token,
  initial,
  onSubmitted,
  onGone,
}: {
  token: string;
  initial: ProviderApplication;
  onSubmitted: () => void;
  onGone: () => void; // link expired / already reviewed mid-session
}) {
  const [fields, setFields] = useState<ProviderApplicationFields>(() => ({
    country: 'US',
    ...stripServerFields(initial),
    source: { channel: 'provider-apply-widget' },
  }));
  const [section, setSection] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [missing, setMissing] = useState<MissingField[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The headshot is uploaded on its own endpoint (not part of the draft save),
  // so it lives outside `fields`: photoUrl is the signed preview, and photo state
  // drives the uploader UI.
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(initial.photoUrl);
  const [photoState, setPhotoState] = useState<'idle' | 'uploading' | 'error'>('idle');
  const [photoError, setPhotoError] = useState<string | null>(null);
  const latest = useRef(fields);
  latest.current = fields;

  const isUS = (fields.country ?? 'US') === 'US';
  const def = SECTIONS[section];

  function patch(p: ProviderApplicationFields) {
    setFields((f) => ({ ...f, ...p }));
    // A previously-flagged required field clears its flag as soon as it's fixed.
    if (missing.length) {
      setMissing((m) => missingRequirements({ ...latest.current, ...p }).filter((x) => m.some((y) => y.field === x.field)));
    }
  }

  async function onPickPhoto(file: File): Promise<void> {
    setPhotoError(null);
    if (!PHOTO_CONTENT_TYPES.includes(file.type)) {
      setPhotoError('Use a JPEG, PNG or WebP image.');
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setPhotoError('That image is over the 5 MB limit.');
      return;
    }
    // Show the local file immediately; the server URL replaces it on success.
    const localPreview = URL.createObjectURL(file);
    setPhotoUrl(localPreview);
    setPhotoState('uploading');
    try {
      const { photoUrl: signed } = await uploadPhoto(token, file);
      setPhotoUrl(signed || localPreview);
      setPhotoState('idle');
      track('provider_apply_photo_uploaded');
    } catch (e) {
      setPhotoState('error');
      if (e instanceof RateLimited) setPhotoError('Too many uploads just now — try again in a minute.');
      else if (e instanceof ApiError && e.status === 413) setPhotoError('That image is over the 5 MB limit.');
      else if (e instanceof ApiError && e.status === 415) setPhotoError('Use a JPEG, PNG or WebP image.');
      else setPhotoError("We couldn't upload that photo. Try again.");
    }
  }

  // Draft save — best-effort, never blocks navigation. Retries once on rate limit.
  async function persist(): Promise<void> {
    setSaveState('saving');
    try {
      await saveApplication(token, toPayload(latest.current));
      setSaveState('saved');
    } catch (e) {
      if (e instanceof RateLimited) {
        setTimeout(() => {
          saveApplication(token, toPayload(latest.current))
            .then(() => setSaveState('saved'))
            .catch(() => setSaveState('error'));
        }, e.retryAfterMs);
        return;
      }
      if (e instanceof ApiError && (e.status === 410 || e.status === 404)) {
        onGone();
        return;
      }
      setSaveState('error');
    }
  }

  function go(next: number) {
    void persist();
    setSection(Math.max(0, Math.min(SECTIONS.length - 1, next)));
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  async function submit() {
    const gaps = missingRequirements(fields);
    if (gaps.length) {
      setMissing(gaps);
      setSubmitError(null);
      go(gaps[0].sectionIndex);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await saveApplication(token, toPayload(fields), true);
      track('provider_apply_submitted');
      onSubmitted();
    } catch (e) {
      if (e instanceof RateLimited) {
        setSubmitError('One sec — too many requests. Try again in a moment.');
      } else if (e instanceof ApiError && e.status === 400) {
        setSubmitError('The server flagged missing required fields. Please review the sections marked with * and try again.');
      } else if (e instanceof ApiError && (e.status === 410 || e.status === 404)) {
        onGone();
        return;
      } else {
        setSubmitError("We couldn't submit just now. Your draft is saved — try again in a minute.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const missingHere = missing.filter((m) => m.sectionIndex === section);
  const has = (f: string) => missingHere.some((m) => m.field === f);

  const footnote =
    saveState === 'saving'
      ? 'Saving your draft…'
      : saveState === 'error'
        ? 'Draft not saved — check your connection. Your answers stay on this page.'
        : 'Saved as a draft. Come back any time from your email link.';

  return (
    <ProviderShell
      progress={(section + 1) / SECTIONS.length}
      rightLabel={`${def.label} · ${section + 1} of ${SECTIONS.length}`}
      onBack={() => go(section - 1)}
      showBack={section > 0}
      footnote={footnote}
    >
      <ScreenHeader
        eyebrow={`Section ${section + 1} of ${SECTIONS.length} — ${def.label}`}
        title={sectionTitle(def, isUS)}
        subtitle={sectionSubtitle(def, isUS)}
      />

      {missingHere.length > 0 && (
        <div style={{ ...card, background: '#FDF3F2', padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#C2554F' }}>Still needed to submit:</div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {missingHere.map((m) => (
              <li key={m.field} style={{ fontSize: 13, fontWeight: 700, color: '#C2554F' }}>
                {m.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {section === 0 && <AboutSection fields={fields} patch={patch} has={has} />}
      {section === 1 && <LicensingSection fields={fields} patch={patch} has={has} isUS={isUS} />}
      {section === 2 && <PracticeSection fields={fields} patch={patch} />}
      {section === 3 && (
        <ProfileSection
          fields={fields}
          patch={patch}
          photoUrl={photoUrl}
          photoState={photoState}
          photoError={photoError}
          onPickPhoto={onPickPhoto}
        />
      )}
      {section === 4 && <PaymentSection fields={fields} patch={patch} has={has} isUS={isUS} />}
      {section === 5 && <AttestationSection fields={fields} patch={patch} />}

      <div style={{ marginTop: 28 }}>
        {section < SECTIONS.length - 1 ? (
          <PrimaryButton full onClick={() => go(section + 1)}>
            Continue
          </PrimaryButton>
        ) : (
          <>
            <PrimaryButton full disabled={submitting} onClick={() => void submit()}>
              {submitting ? 'Submitting…' : 'Submit my application'}
            </PrimaryButton>
            {missing.length > 0 && (
              <div style={{ fontSize: 13, fontWeight: 700, color: '#C2554F', marginTop: 12, textAlign: 'center' }}>
                A few required fields are missing — we've taken you to the first one.
              </div>
            )}
            {submitError && (
              <div style={{ fontSize: 13, fontWeight: 700, color: '#C2554F', marginTop: 12, textAlign: 'center' }}>{submitError}</div>
            )}
          </>
        )}
      </div>
    </ProviderShell>
  );
}

// Server-owned keys never echoed back into the POST payload.
function stripServerFields(app: ProviderApplication): ProviderApplicationFields {
  // photoFileId/photoUrl are server-owned (set by the upload endpoint, presigned
  // on read) and must not be echoed back on save.
  const {
    status: _status, email: _email, photoFileId: _photoFileId, photoUrl: _photoUrl, ...rest
  } = app;
  return rest;
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------
type SectionProps = {
  fields: ProviderApplicationFields;
  patch: (p: ProviderApplicationFields) => void;
  has?: (field: string) => boolean;
};

function AboutSection({ fields, patch, has }: SectionProps) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="First name" required error={has?.('firstName')}>
          <TextInput value={fields.firstName ?? ''} onChange={(v) => patch({ firstName: v })} autoComplete="given-name" error={has?.('firstName')} />
        </Field>
        <Field label="Last name" required error={has?.('lastName')}>
          <TextInput value={fields.lastName ?? ''} onChange={(v) => patch({ lastName: v })} autoComplete="family-name" error={has?.('lastName')} />
        </Field>
      </div>
      <Field label="Credential" hint="What you put after your name, e.g. DPT or PT, OCS. Optional.">
        <TextInput value={fields.credential ?? ''} onChange={(v) => patch({ credential: v })} placeholder="DPT" />
      </Field>
      <Field label="Phone" hint="For review questions only — patients never see it.">
        <TextInput value={fields.phone ?? ''} onChange={(v) => patch({ phone: v })} type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 010-2030" />
      </Field>
      <Field label="Where do you practice?">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <SelectRow label="United States" selected={(fields.country ?? 'US') === 'US'} onSelect={() => patch({ country: 'US' })} />
          {/* Leaving the US clears US-only licensure, so a change of mind can't
              submit state licences the provider can no longer see or edit. An
              emptied array is transmitted as a deliberate clear (see toPayload),
              unlike an undefined scalar — which is why NPI is left to the server. */}
          <SelectRow
            label="United Kingdom"
            selected={fields.country === 'GB'}
            onSelect={() => patch({ country: 'GB', licensedStates: [], compactPrivileges: [] })}
          />
        </div>
      </Field>
      {(fields.country ?? 'US') === 'US' && (
        <Field label="NPI" hint="Your 10-digit National Provider Identifier. Optional, but it speeds up verification.">
          <TextInput value={fields.npi ?? ''} onChange={(v) => patch({ npi: v.replace(/\D/g, '').slice(0, 10) })} inputMode="numeric" placeholder="1234567890" />
        </Field>
      )}
    </>
  );
}

// "Licence" is the US word for it. Outside the US the same section is asking
// about a national registration, and a heading that says "Your license" over a
// field labelled "Registration number" reads as a form built for someone else.
function sectionTitle(def: { id: string; title: string }, isUS: boolean): string {
  if (!isUS && def.id === 'licensing') return 'Your registration.';
  return def.title;
}

function sectionSubtitle(def: { id: string; subtitle?: string }, isUS: boolean): string | undefined {
  if (!isUS && def.id === 'licensing') {
    return 'We verify every registration before a profile goes live — this is the part we check first.';
  }
  return def.subtitle;
}

// State licensure and the PT Compact are US constructs. A UK physiotherapist
// holds one HCPC registration covering the whole country, so asking which states
// they are licensed in reads as a form that wasn't built for them — and the
// question was REQUIRED, which made the application unsubmittable outside the US.
function LicensingSection({ fields, patch, has, isUS }: SectionProps & { isUS: boolean }) {
  return (
    <>
      <Field
        label={isUS ? 'License number' : 'Registration number'}
        required
        error={has?.('licenseNumber')}
        hint={isUS ? undefined : 'Your HCPC registration number, or the equivalent from your national regulator.'}
      >
        <TextInput value={fields.licenseNumber ?? ''} onChange={(v) => patch({ licenseNumber: v })} error={has?.('licenseNumber')} />
      </Field>
      <Field
        label={isUS ? 'License expiry date' : 'Registration renewal date'}
        required
        error={has?.('licenseExpiresOn')}
      >
        <TextInput value={fields.licenseExpiresOn ?? ''} onChange={(v) => patch({ licenseExpiresOn: v })} type="date" error={has?.('licenseExpiresOn')} />
      </Field>
      {isUS && (
        <>
          <Field
            label="States where you're licensed"
            required
            error={has?.('licensedStates')}
            hint="Pick every state where you hold a full PT license."
          >
            <ChipMultiSelect
              options={US_STATE_NAMES}
              values={fields.licensedStates ?? []}
              onChange={(v) => patch({ licensedStates: v })}
              columns={3}
              error={has?.('licensedStates')}
            />
          </Field>
          <Field
            label="Compact privileges"
            hint="Compact privileges are purchased per state; select the states where you hold an active privilege."
          >
            <ChipMultiSelect
              options={US_STATE_NAMES}
              values={fields.compactPrivileges ?? []}
              onChange={(v) => patch({ compactPrivileges: v })}
              columns={3}
            />
          </Field>
        </>
      )}
    </>
  );
}

function PracticeSection({ fields, patch }: SectionProps) {
  return (
    <>
      <Field label="Specialties" hint="The APTA board-certified specialty areas. Pick all that apply.">
        <ChipMultiSelect options={SPECIALTIES} values={fields.specialties ?? []} onChange={(v) => patch({ specialties: v })} />
      </Field>
      <Field label="Conditions you treat" hint="Type a condition and press Enter — e.g. ACL recovery, frozen shoulder, sciatica.">
        <TagInput values={fields.conditionsTreated ?? []} onChange={(v) => patch({ conditionsTreated: v })} placeholder="Add a condition…" />
      </Field>
      <Field label="Languages you practice in">
        <TagInput values={fields.languages ?? []} onChange={(v) => patch({ languages: v })} placeholder="English, Spanish…" />
      </Field>
      <Field label="Years in practice">
        <TextInput
          value={fields.yearsInPractice != null ? String(fields.yearsInPractice) : ''}
          onChange={(v) => {
            const n = parseInt(v.replace(/\D/g, ''), 10);
            patch({ yearsInPractice: Number.isFinite(n) ? n : undefined });
          }}
          inputMode="numeric"
          placeholder="8"
        />
      </Field>
      <Field label="Clinic / practice location" hint="City and state is enough — e.g. “Sacramento, CA” or “Telehealth only”.">
        <TextInput value={fields.clinicLocation ?? ''} onChange={(v) => patch({ clinicLocation: v })} placeholder="Sacramento, CA" />
      </Field>
    </>
  );
}

function PhotoUploader({
  photoUrl, state, error, onPick,
}: {
  photoUrl?: string;
  state: 'idle' | 'uploading' | 'error';
  error: string | null;
  onPick: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <div
          style={{
            width: 84,
            height: 84,
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
            background: 'var(--teal-tint)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {photoUrl ? (
            <img src={photoUrl} alt="Your headshot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <svg width={34} height={34} viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle cx={12} cy={8} r={4} stroke="var(--teal-deep)" strokeWidth={1.6} />
              <path d="M4 20c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" stroke="var(--teal-deep)" strokeWidth={1.6} strokeLinecap="round" />
            </svg>
          )}
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onPick(file);
              e.target.value = ''; // allow re-selecting the same file
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={state === 'uploading'}
            style={{
              border: '1.5px solid var(--teal-deep)',
              color: 'var(--teal-deep)',
              background: 'transparent',
              fontWeight: 700,
              fontSize: 14,
              padding: '9px 16px',
              borderRadius: 10,
              cursor: state === 'uploading' ? 'default' : 'pointer',
              opacity: state === 'uploading' ? 0.6 : 1,
            }}
          >
            {state === 'uploading' ? 'Uploading…' : photoUrl ? 'Replace photo' : 'Upload a photo'}
          </button>
        </div>
      </div>
      {error && (
        <div style={{ color: '#C2554F', fontSize: 13, fontWeight: 600, marginTop: 10 }}>{error}</div>
      )}
    </div>
  );
}

type ProfileSectionProps = SectionProps & {
  photoUrl?: string;
  photoState: 'idle' | 'uploading' | 'error';
  photoError: string | null;
  onPickPhoto: (file: File) => void;
};

function ProfileSection({
  fields, patch, photoUrl, photoState, photoError, onPickPhoto,
}: ProfileSectionProps) {
  const areas: ExpertiseArea[] = fields.expertiseAreas?.length ? fields.expertiseAreas : [{ title: '', description: '' }];
  const setArea = (i: number, p: Partial<ExpertiseArea>) => {
    const next = areas.map((a, j) => (j === i ? { ...a, ...p } : a));
    patch({ expertiseAreas: next });
  };
  return (
    <>
      <Field label="Headshot" hint="A clear, friendly photo patients will see on your profile. JPEG, PNG or WebP, up to 5 MB. Optional.">
        <PhotoUploader photoUrl={photoUrl} state={photoState} error={photoError} onPick={onPickPhoto} />
      </Field>
      <Field label="Headline" hint="One line under your name, e.g. “Orthopedic & sports rehabilitation”. Optional.">
        <TextInput value={fields.headline ?? ''} onChange={(v) => patch({ headline: v })} placeholder="Orthopedic & sports rehabilitation" />
      </Field>
      <Field label="Practice name" hint="The clinic or practice you want shown on your profile. Optional.">
        <TextInput value={fields.practiceName ?? ''} onChange={(v) => patch({ practiceName: v })} placeholder="Downtown Physical Therapy" />
      </Field>
      <Field label="Bio" hint="2–4 sentences. Who you are, how long you've practiced, what patients say about working with you.">
        <TextArea value={fields.bio ?? ''} onChange={(v) => patch({ bio: v })} rows={5} placeholder="I'm a doctor of physical therapy with…" />
      </Field>
      <Field label="Areas of expertise" hint="Short titled highlights for your profile — e.g. “Post-surgical knees” with a sentence on your approach.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {areas.map((a, i) => (
            <div key={i} style={{ ...card, padding: 16 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  value={a.title}
                  onChange={(e) => setArea(i, { title: e.target.value })}
                  placeholder="Title — e.g. Post-surgical knees"
                  style={{ border: 'none', outline: 'none', flex: 1, fontSize: 16, fontWeight: 800, background: 'transparent' }}
                />
                {areas.length > 1 && (
                  <button
                    onClick={() => patch({ expertiseAreas: areas.filter((_, j) => j !== i) })}
                    aria-label="Remove this area"
                    style={{ color: 'var(--slate)', fontWeight: 800, fontSize: 18, lineHeight: 1 }}
                  >
                    ×
                  </button>
                )}
              </div>
              <textarea
                value={a.description}
                onChange={(e) => setArea(i, { description: e.target.value })}
                placeholder="A sentence or two on your experience here."
                rows={2}
                style={{ border: 'none', outline: 'none', width: '100%', fontSize: 14, fontWeight: 600, background: 'transparent', resize: 'vertical', marginTop: 8, fontFamily: 'inherit', lineHeight: 1.5, color: 'var(--ink)' }}
              />
            </div>
          ))}
          <button
            onClick={() => patch({ expertiseAreas: [...areas, { title: '', description: '' }] })}
            style={{ ...card, padding: '13px 16px', fontSize: 14, fontWeight: 800, color: 'var(--teal-deep)', textAlign: 'left' }}
          >
            + Add another area
          </button>
        </div>
      </Field>
    </>
  );
}

function PaymentSection({ fields, patch, has, isUS }: SectionProps & { isUS: boolean }) {
  const plans = fields.acceptedInsurancePlans ?? [];
  const canonical = useMemo(() => plans.filter((p) => INSURANCE_CARRIERS.includes(p)), [plans]);
  const custom = useMemo(() => plans.filter((p) => !INSURANCE_CARRIERS.includes(p)), [plans]);
  return (
    <>
      {isUS ? (
        <Field
          label="Insurance you accept"
          required
          error={has?.('acceptedInsurancePlans')}
          hint="Patients are matched on this list, so only include plans you actively bill."
        >
          <ChipMultiSelect
            options={INSURANCE_CARRIERS}
            values={canonical}
            onChange={(v) => patch({ acceptedInsurancePlans: [...v, ...custom] })}
            error={has?.('acceptedInsurancePlans')}
          />
          <div style={{ marginTop: 10 }}>
            <TagInput
              values={custom}
              onChange={(v) => patch({ acceptedInsurancePlans: [...canonical, ...v] })}
              placeholder="Other plan not listed — type it and press Enter"
            />
          </div>
        </Field>
      ) : (
        <Field label="Insurance you accept" hint="Optional outside the US — add any insurers or schemes you bill.">
          <TagInput values={plans} onChange={(v) => patch({ acceptedInsurancePlans: v })} placeholder="Add an insurer…" />
        </Field>
      )}
      <Field
        label={`Cash rate per session (${isUS ? 'USD' : 'GBP'})`}
        hint="What a self-pay patient pays for a standard session."
      >
        <TextInput
          value={fields.cashRate != null ? String(fields.cashRate) : ''}
          onChange={(v) => {
            const n = parseFloat(v.replace(/[^0-9.]/g, ''));
            patch({ cashRate: Number.isFinite(n) ? n : undefined });
          }}
          inputMode="decimal"
          placeholder="120"
        />
      </Field>
      <Field label="Session lengths you offer">
        <ChipMultiSelect
          options={SESSION_DURATIONS.map((d) => `${d} min`)}
          values={(fields.sessionDurations ?? []).map((d) => `${d} min`)}
          onChange={(v) => patch({ sessionDurations: v.map((s) => parseInt(s, 10)).sort((a, b) => a - b) })}
          columns={3}
        />
      </Field>
    </>
  );
}

function AttestationSection({ fields, patch }: SectionProps) {
  return (
    <>
      <div style={{ ...card, padding: 18, marginBottom: 20 }}>
        <CheckRow checked={!!fields.malpracticeAttested} onChange={(v) => patch({ malpracticeAttested: v })}>
          I carry professional liability insurance covering my practice, including telehealth.
        </CheckRow>
      </div>
      {fields.malpracticeAttested && (
        <>
          <Field label="Malpractice carrier" hint="Optional — it helps us verify faster.">
            <TextInput value={fields.malpracticeCarrier ?? ''} onChange={(v) => patch({ malpracticeCarrier: v })} placeholder="e.g. HPSO" />
          </Field>
          <Field label="Policy expiry date">
            <TextInput value={fields.malpracticeExpiresOn ?? ''} onChange={(v) => patch({ malpracticeExpiresOn: v })} type="date" />
          </Field>
        </>
      )}
      <div className="pretty" style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', lineHeight: 1.55, marginTop: 4 }}>
        By submitting, you attest that everything in this application is true and accurate to the best of your knowledge,
        and that you'll keep your licensing information current if it changes.
      </div>
    </>
  );
}
